import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import Notification from '../components/ui/Notification';
import { notificationApi, NOTIFICATION_CATEGORIES, notificationUtils } from '../api/notifications';
import { useUser } from './UserContext';
import { getAuth } from 'firebase/auth';

const SSE_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const POLLING_INTERVAL_WITH_SSE = 120000; // 2 minutes fallback when SSE connected
const POLLING_INTERVAL_WITHOUT_SSE = 30000; // 30 seconds when SSE disconnected

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  // ==================== USER AUTHENTICATION STATE ====================
  const { user } = useUser();
  
  // ==================== EXISTING TOAST NOTIFICATION STATE ====================
  const [notifications, setNotifications] = useState([]);

  // ==================== NEW IN-APP NOTIFICATION SYSTEM STATE ====================
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadCountByCategory, setUnreadCountByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [error, setError] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  
  // Polling interval reference
  const pollingIntervalRef = useRef(null);
  const isPollingActiveRef = useRef(false);
  
  // SSE references
  const sseRef = useRef(null);
  const sseReconnectTimeoutRef = useRef(null);
  const sseReconnectAttempts = useRef(0);
  const SSE_MAX_RECONNECT_ATTEMPTS = 5;

  // ==================== EXISTING TOAST NOTIFICATION METHODS ====================
  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'success',
      autoClose: true,
      duration: 5000,
      position: 'top-right',
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove if autoClose is enabled
    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods for toast notifications
  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({ ...options, type: 'success', message });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    return addNotification({ ...options, type: 'error', message, duration: 8000 });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({ ...options, type: 'warning', message });
  }, [addNotification]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({ ...options, type: 'info', message });
  }, [addNotification]);

  // ==================== NEW IN-APP NOTIFICATION METHODS ====================

  /**
   * Fetch notifications from backend with comprehensive error handling
   */
  const fetchNotifications = useCallback(async (params = {}) => {
    if (!user) {
      return { notifications: [], pagination: {} };
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationApi.getNotifications({
        limit: 20,
        offset: 0,
        ...params
      });
      
      setInAppNotifications(response.notifications || []);
      setLastFetchTime(new Date());
      
      return response;
    } catch (err) {
      setError('Failed to load notifications');
      return { notifications: [], pagination: {} };
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch unread count with category breakdown
   * Optimized: fetches total count in 1 API call; category breakdown only on explicit request
   */
  const fetchUnreadCount = useCallback(async (category = null) => {
    if (!user) {
      return 0;
    }
    
    try {
      if (category) {
        const response = await notificationApi.getUnreadCount(category);
        const count = response.unreadCount || 0;
        setUnreadCountByCategory(prev => ({
          ...prev,
          [category]: count
        }));
        return count;
      } else {
        // Single API call for total unread count
        const response = await notificationApi.getUnreadCount();
        const totalCount = response.unreadCount || 0;
        setUnreadCount(totalCount);
        
        // Use category breakdown from response if backend provides it,
        // otherwise skip the per-category calls during polling
        if (response.countByCategory) {
          setUnreadCountByCategory(response.countByCategory);
        }
        
        return totalCount;
      }
    } catch (err) {
      if (category) {
        setUnreadCountByCategory(prev => ({ ...prev, [category]: 0 }));
      } else {
        setUnreadCount(0);
        setUnreadCountByCategory({});
      }
      return 0;
    }
  }, [user]);

  /**
   * Fetch full category breakdown (used on page load, not during polling)
   */
  const fetchCategoryBreakdown = useCallback(async () => {
    if (!user) return;
    
    try {
      const categoryPromises = Object.values(NOTIFICATION_CATEGORIES).map(async (cat) => {
        try {
          const catResponse = await notificationApi.getUnreadCount(cat);
          return { category: cat, count: catResponse.unreadCount || 0 };
        } catch {
          return { category: cat, count: 0 };
        }
      });
      
      const categoryResults = await Promise.all(categoryPromises);
      const categoryBreakdown = categoryResults.reduce((acc, { category, count }) => {
        acc[category] = count;
        return acc;
      }, {});
      
      setUnreadCountByCategory(categoryBreakdown);
    } catch {
      // Fail silently
    }
  }, [user]);

  /**
   * Mark notification as read with optimistic updates
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      // Check if notification is already read before doing optimistic update
      let wasAlreadyRead = false;
      setInAppNotifications(prev =>
        prev.map(n => {
          if (n._id === notificationId) {
            wasAlreadyRead = n.read;
            return n.read ? n : { ...n, read: true, readAt: new Date() };
          }
          return n;
        })
      );
      
      // Only decrement if it was actually unread
      if (!wasAlreadyRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      await notificationApi.markAsRead(notificationId);
      await fetchUnreadCount();
      
      return true;
    } catch (err) {
      // Revert optimistic update on error (only if we changed it)
      setInAppNotifications(prev =>
        prev.map(n =>
          n._id === notificationId
            ? { ...n, read: false, readAt: null }
            : n
        )
      );
      
      showError('Failed to mark notification as read');
      return false;
    }
  }, [fetchUnreadCount, showError]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async (type = null) => {
    try {
      const response = await notificationApi.markAllAsRead(type);
      
      setInAppNotifications(prev =>
        prev.map(n => 
          !type || n.type === type 
            ? { ...n, read: true, readAt: new Date() }
            : n
        )
      );
      
      if (type) {
        await fetchUnreadCount();
      } else {
        setUnreadCount(0);
        setUnreadCountByCategory({});
      }
      
      showSuccess(`${response.modifiedCount || 0} notifications marked as read`);
      return true;
    } catch (err) {
      showError('Failed to mark notifications as read');
      return false;
    }
  }, [fetchUnreadCount, showSuccess, showError]);

  /**
   * Delete notification (uses functional state update to avoid stale closure)
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      // Capture whether notification is unread before removing from state
      let wasUnread = false;
      setInAppNotifications(prev => {
        const notification = prev.find(n => n._id === notificationId);
        wasUnread = notification ? !notification.read : false;
        return prev.filter(n => n._id !== notificationId);
      });
      
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      await notificationApi.deleteNotification(notificationId);
      showSuccess('Notification deleted');
      return true;
    } catch (err) {
      showError('Failed to delete notification');
      return false;
    }
  }, [showSuccess, showError]);

  /**
   * Fetch user preferences
   */
  const fetchPreferences = useCallback(async () => {
    if (!user) {
      return null;
    }
    
    try {
      const response = await notificationApi.getPreferences();
      setPreferences(response.preferences);
      return response.preferences;
    } catch (err) {
      
      // Set default preferences on error
      const defaultPreferences = {
        globalOptOut: false,
        preferredLanguage: 'en',
        channelPreferences: {
          inApp: true,
          email: true
        },
        categoryPreferences: Object.values(NOTIFICATION_CATEGORIES).reduce((acc, category) => {
          acc[category] = {
            enabled: true,
            channels: ['in_app', 'email'],
            frequency: 'immediate'
          };
          return acc;
        }, {}),
        quietHours: {
          enabled: false,
          startTime: '22:00',
          endTime: '08:00',
          timezone: 'UTC',
          respectForChannels: ['email']
        },
        digestPreferences: {
          enabled: false,
          frequency: 'daily',
          time: '09:00',
          categories: [],
          minNotifications: 3
        }
      };
      
      setPreferences(defaultPreferences);
      return defaultPreferences;
    }
  }, [user]);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      const response = await notificationApi.updatePreferences(newPreferences);
      setPreferences(response.preferences);
      showSuccess('Notification preferences updated');
      return response.preferences;
    } catch (err) {
      showError('Failed to update preferences');
      return null;
    }
  }, [showSuccess, showError]);

  /**
   * Start polling for notifications (interval adapts based on SSE status)
   */
  const startPolling = useCallback(() => {
    if (isPollingActiveRef.current) return;
    
    isPollingActiveRef.current = true;
    fetchUnreadCount();
    
    const interval = sseConnected ? POLLING_INTERVAL_WITH_SSE : POLLING_INTERVAL_WITHOUT_SSE;
    pollingIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, interval);
  }, [fetchUnreadCount, sseConnected]);

  /**
   * Stop polling for notifications
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingActiveRef.current = false;
  }, []);

  /**
   * Refresh all notification data (includes category breakdown on explicit refresh)
   */
  const refreshNotifications = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount(),
      fetchCategoryBreakdown(),
      fetchPreferences()
    ]);
  }, [fetchNotifications, fetchUnreadCount, fetchCategoryBreakdown, fetchPreferences]);

  // ==================== SSE REAL-TIME INTEGRATION ====================

  useEffect(() => {
    if (!user) {
      // Cleanup SSE on logout
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      clearTimeout(sseReconnectTimeoutRef.current);
      setSseConnected(false);
      sseReconnectAttempts.current = 0;
      return;
    }

    const connectSSE = () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        const userId = currentUser?.uid || user._id || user.uid;
        if (!userId) return;

        const url = `${SSE_BASE_URL}/sse/user/${userId}?userId=${userId}`;
        const eventSource = new EventSource(url);
        sseRef.current = eventSource;

        eventSource.onopen = () => {
          setSseConnected(true);
          sseReconnectAttempts.current = 0;
        };

        eventSource.addEventListener('notification', (e) => {
          try {
            const data = JSON.parse(e.data);
            // Add new notification to the front of the list
            setInAppNotifications(prev => [data, ...prev]);
            setUnreadCount(prev => prev + 1);
            if (data.category) {
              setUnreadCountByCategory(prev => ({
                ...prev,
                [data.category]: (prev[data.category] || 0) + 1
              }));
            }
          } catch {
            // Silently ignore parse errors
          }
        });

        eventSource.addEventListener('connected', () => {
          setSseConnected(true);
        });

        eventSource.onerror = () => {
          setSseConnected(false);
          eventSource.close();
          sseRef.current = null;

          // Exponential backoff reconnect
          if (sseReconnectAttempts.current < SSE_MAX_RECONNECT_ATTEMPTS) {
            sseReconnectAttempts.current += 1;
            const delay = Math.min(1000 * Math.pow(2, sseReconnectAttempts.current), 30000);
            sseReconnectTimeoutRef.current = setTimeout(connectSSE, delay);
          }
        };
      } catch {
        setSseConnected(false);
      }
    };

    connectSSE();

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      clearTimeout(sseReconnectTimeoutRef.current);
      setSseConnected(false);
      sseReconnectAttempts.current = 0;
    };
  }, [user]);

  // ==================== LIFECYCLE EFFECTS ====================

  // Restart polling when SSE status changes (adjust interval)
  useEffect(() => {
    if (!user) return;
    
    stopPolling();
    startPolling();
    
    return () => {
      stopPolling();
    };
  }, [user, sseConnected, startPolling, stopPolling]);

  // Initial data fetch when user authenticates
  useEffect(() => {
    if (!user) {
      stopPolling();
      setInAppNotifications([]);
      setUnreadCount(0);
      setUnreadCountByCategory({});
      setPreferences(null);
      setError(null);
      return;
    }
    
    refreshNotifications();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle visibility change to pause/resume polling
  useEffect(() => {
    if (!user) return;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
        refreshNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, startPolling, stopPolling, refreshNotifications]);

  // ==================== CONTEXT VALUE ====================
  const value = {
    // Existing toast notification methods (backward compatibility)
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // New in-app notification system
    inAppNotifications,
    unreadCount,
    unreadCountByCategory,
    loading,
    preferences,
    error,
    lastFetchTime,
    
    // New notification methods
    fetchNotifications,
    fetchUnreadCount,
    fetchCategoryBreakdown,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchPreferences,
    updatePreferences,
    refreshNotifications,
    
    // Polling control
    startPolling,
    stopPolling,
    
    // SSE status
    sseConnected,
    
    // Utility methods
    utils: notificationUtils
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Render existing toast notifications */}
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          type={notification.type}
          message={notification.message}
          position={notification.position}
          autoClose={false} // We handle auto-close in context
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;





